import React, { useState, useEffect, useRef, useContext, useReducer } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { firebaseAuth } from "../../../providers/AuthProvider";
import firebase from "firebase/compat/app";
import "firebase/storage";
import { db } from "../../../firebase";
import "firebase/functions";
import {
  CheckIcon,
} from "../../../Constants";
import "../../../assets/css/groups.css";
import { toast } from "react-toastify";
import "../../../assets/css/registrations.css";
import DOMPurify from "dompurify";
import * as html2canvas from 'html2canvas';
import { jsPDF } from "jspdf";
import * as JSZip from "jszip";
import * as JSZipUtils from "jszip-utils";
import BarcodeReader from "react-barcode-reader";
import moment from "moment";
import { saveAs } from "file-saver";
import JoditEditor from "jodit-react";
import { CurrentEventDataContext } from "../CurrentEventDataContext";
import { getModelInterestLabel } from "../../../utils/modelInterest";
import { useModelInterestCatalog } from "../../../hooks/useModelInterestCatalog";
import RegistrationActions from './RegistrationActions';

import { DEFAULT_REG_FILTERS, DEFAULT_SORT_ORDER, DEFAULT_FIELDS, BASE_AVAILABLE_FIELDS,
  readRegistrationsTableSetting, writeRegistrationsTableSetting, normalizeRegFilters,
  filterRegistrations, sortRegistrations, normalizeSortOrder, clampPageIndex, fieldLabel } from '../../../utils/registrationTable';

const getRegistrationStatusLabel = (registration) => {
  if (!registration?.status) return "Not set";
  if (registration.status === "Registered" && registration.isConfirmed) return "Confirmed";
  if (registration.status === "Cancelled" && registration.isNoShow) return "No Show-Cancelled";
  if (registration.status === "Cancelled" && registration.isConfirmed) return "Self-Cancelled";

  return registration.status;
};

const Pagination = ({ regsPerPage, totalRegs, paginate, currentPage }) => {
  const totalPages = Math.ceil(totalRegs / regsPerPage);
  if (totalPages <= 1) return null;
  const pages = [...new Set([1, ...Array.from({ length: 5 }, (_, index) => currentPage - 2 + index)
    .filter((page) => page > 1 && page < totalPages), totalPages])];
  return <nav aria-label="Registration pagination" className="mt-4 flex flex-wrap items-center gap-2">
    <button type="button" className="registration-page-button" disabled={currentPage === 1} onClick={() => paginate(currentPage - 1)}>Previous</button>
    {pages.map((page, index) => <React.Fragment key={page}>
      {index > 0 && page - pages[index - 1] > 1 && <span aria-hidden="true">…</span>}
      <button type="button" aria-label={`Page ${page}`} aria-current={page === currentPage ? 'page' : undefined}
        className="registration-page-button" onClick={() => paginate(page)}>{page}</button>
    </React.Fragment>)}
    <button type="button" className="registration-page-button" disabled={currentPage === totalPages} onClick={() => paginate(currentPage + 1)}>Next</button>
    <span className="ml-2 text-sm tabular-nums text-zinc-500 dark:text-zinc-400">Page {currentPage} of {totalPages}</span>
  </nav>;
};

const Registrations = ({ id }) => {
  const modelInterestCatalog = useModelInterestCatalog();
  const storage = firebase.storage().ref();
  const history = useNavigate();
  const [regFilters, setRegFilters] = useState(() =>
    normalizeRegFilters(readRegistrationsTableSetting(id, "regFilters", DEFAULT_REG_FILTERS))
  );
  const [allSelected, setAllSelected] = useState(false);
  const [importReg, setImportReg] = useState(false);
  const [createReminder, setCreateReminder] = useState(false);
  const [createReminderSMS, setCreateReminderSMS] = useState(false);
  const [showFieldList, setShowFieldList] = useState(false);
  const [storageUnavailable, setStorageUnavailable] = useState(false);
  const fieldPicker = useRef(null);
  const settingsRef = useRef({ regFilters, sortOrder: DEFAULT_SORT_ORDER });
  const requestGeneration = useRef(0);
  const [availableFieldList, setAvailableFieldList] = useState(BASE_AVAILABLE_FIELDS);
  const [sending, setSending] = useState(0);
  const [commItem, setCommItem] = useState(false);
  const [emailHtml, setEmailHtml] = useState("");
  const [smsBody, setSMSBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [smsTemplate, setSMSTemplate] = useState("");
  const editor = useRef(null);
  const [previewEmailHtml, setPreviewEmailHtml] = useState(null);
  const [previewSMS, setPreviewSMS] = useState(null);
  const [reminderEmailList, setReminderEmailList] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [regCount, setRegCount] = useState(false);
  const [regItemCounts, setRegItemCounts] = useState([]);
  const [sortOrder, setSortOrder] = useState(() =>
    normalizeSortOrder(readRegistrationsTableSetting(id, "sortOrder", DEFAULT_SORT_ORDER))
  );
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [sessionList, setSessionList] = useState([]);
  const [dayList, setDayList] = useState([]);
  const { user } = useContext(firebaseAuth);
  const reducer = (state, payload) => ({ ...state, ...payload });
  const [eventData, setEventData] = useState(null);
  const { setCurrentEventData } = useContext(CurrentEventDataContext);
  const [fieldList, setFieldList] = useState(DEFAULT_FIELDS);
  settingsRef.current = { regFilters, sortOrder };
  const [data, setData] = useReducer(reducer, {
    registrations: [],
    isFetching: true,
    message: null,
  });

  const [regsPerPage, setRegsPerPage] = useState(() => {
    const stored = readRegistrationsTableSetting(id, 'pageSize', 100);
    return [50, 100, 250, 500].includes(stored) ? stored : 100;
  });
  const currentPageIndex = clampPageIndex(regFilters.pageIndex, data.filtered?.length || 0, regsPerPage);

  // Get current regs
  const indexOfLastReg = (currentPageIndex + 1) * regsPerPage;
  const indexOfFirstReg = indexOfLastReg - regsPerPage;
  const currentRegs = data.filtered?.slice(indexOfFirstReg, indexOfLastReg);

  // Change page
  const paginate = pageNumber => setRegFilters({ ...regFilters, pageIndex: pageNumber - 1 });

  const getPasscodeDescription = (passcode) => {
    let desc = eventData?.multiUsePasscodes?.find(o => o.passcode?.toUpperCase() === passcode?.toUpperCase())?.description
    if (desc) return " - " + desc; else return "";
  }
  const getSessionItem = (sessionId) => {
    let session = eventData?.sessionItems?.find(o => o.id === sessionId);
    if (session) return session.name; else return "";
  }
  const downloadPdfDocuments = async () => {
    if (data?.filtered?.length > 0) {
      if (!emailHtml) {
        alert('No email message found.');
        return;
      }
      if (!emailSubject) {
        alert('No email subject found.');
        return;
      }
      let total = reminderEmailList.length;
      let success = 0;
      let incomplete = 0;
      let failed = 0;
      // setSending(1);
      var zip = new JSZip();
      // setPreviewEmailHtml('Archiving ' + 0 + ' of ' + total + ', please wait.');
      for (var i = 0; i < reminderEmailList.length; i++) {
        let x = reminderEmailList[i];
        // setPreviewEmailHtml('Sending ' + i + ' of ' + total + ', please wait.');
        if (emailHtml && emailSubject) {
          var sendEmail = firebase
            .functions()
            .httpsCallable("appV2/sendConfirmationEmail");
          await sendEmail({ reg: data?.filtered[x], eventData: eventData, preview: true, nofooter: true, html: emailHtml, subject: emailSubject })
            // eslint-disable-next-line no-loop-func -- counters (failed/success) are intentionally shared across iterations
            .then((result) => {
              if (result?.data.error) {
                toast.warning(result?.data.message);
                failed++;
              }
              else {
                var html_string = result?.data.message;
                var iframe = document.createElement('iframe');
                document.body.appendChild(iframe);
                setTimeout(function () {
                  var iframedoc = iframe.contentDocument || iframe.contentWindow.document;
                  iframedoc.body.innerHTML = html_string;
                  html2canvas(iframedoc.body, {
                    useCORS: true,
                    allowTaint: false,
                    scale: 1,
                    width: 800,
                    windowWidth: 800
                  }).then(function (canvas) {
                    success++;
                    iframe.remove();
                    var width = canvas.width;
                    var height = canvas.height;
                    var millimeters = {};
                    millimeters.width = Math.floor(width * 0.264583);
                    millimeters.height = Math.floor(height * 0.264583);
                    if (millimeters.height > 297) {
                      let ratio = 297 / millimeters.height;
                      millimeters.height = 297;
                      millimeters.width = millimeters.width * ratio;
                    }
                    var imgData = canvas.toDataURL(
                      'image/jpeg', 1.0);
                    var pdf = new jsPDF("p", "mm", "a4");
                    // pdf.setFont('Helvetica');
                    pdf.addImage(imgData, 'JPEG', 0, 0, millimeters.width, millimeters.height);  // 180x150 mm @ (10,10)mm
                    // pdf.save('sample-file.pdf');
                    zip.file(data?.filtered[x].id + '.pdf', pdf.output('blob'));
                    if (incomplete + failed + success === total) {
                      zip.generateAsync({ type: 'blob' }).then(function (content) {
                        saveAs(content, 'reports.zip');
                      });
                    }
                  });
                }, 10);
              }
            })
            .catch((e) => {
              // Sentry.captureException(e);
              toast.error(e.message);
            });
        } else {
          incomplete++;
        }
      }
      // setSending(2);
      // setPreviewEmailHtml("Send complete.<br/>Success: " + success + "<br/>Incomplete: " + incomplete + "<br/>Failed: " + failed + "<br/>Total: " + total + "<br/>");
      // setEmailHtml("");
      // setEmailSubject("");
      setReminderEmailList([]);
      var ele = document.getElementsByName('reminderCheck');
      for (let i = 0; i < ele.length; i++) {
        if (ele[i].type === 'checkbox')
          ele[i].checked = false;
      }
    }
  }
  const filter = (tmpData) => {
    const settings = settingsRef.current;
    const registrations = sortRegistrations(tmpData.registrations || [], settings.sortOrder || DEFAULT_SORT_ORDER,
      (registration) => moment(`${registration.sessionDate || ''} ${registration.sessionTime || ''}`, 'dddd MMMM Do YYYY h:mm A').valueOf());
    const filtered = filterRegistrations(registrations, settings.regFilters);
    setData({ ...tmpData, registrations, filtered, isFetching: false, filter: false });
  };
  const previewEmail = async () => {
    if (data?.filtered?.length > 0) {
      var sendEmail = firebase
        .functions()
        .httpsCallable("appV2/sendConfirmationEmail");
      sendEmail({ reg: data?.filtered[0], eventData: eventData, preview: true, html: emailHtml, subject: 'Test Subject' })
        .then((result) => {
          if (result?.data.error) {
            toast.warning(result?.data.message);
            return;
          }
          else {
            setPreviewEmailHtml(result?.data.message);
          }
        })
        .catch((e) => {
          // Sentry.captureException(e);
          toast.error(e.message);
        });
    }
  }
  const sendEmail = async () => {
    if (data?.filtered?.length > 0) {
      if (!emailHtml) {
        alert('No email message found.');
        return;
      }
      if (!emailSubject) {
        alert('No email subject found.');
        return;
      }
      let total = reminderEmailList.length;
      let success = 0;
      let incomplete = 0;
      let failed = 0;
      setSending(1);
      setPreviewEmailHtml('Sending ' + 0 + ' of ' + total + ', please wait.');
      for (var i = 0; i < reminderEmailList.length; i++) {
        let x = reminderEmailList[i];
        setPreviewEmailHtml('Sending ' + i + ' of ' + total + ', please wait.');
        console.log('sending email to', data?.filtered[x].email);
        
        if (data?.filtered[x].email && emailHtml && emailSubject) {
          var sendEmail = firebase
            .functions()
            .httpsCallable("appV2/sendConfirmationEmail");
          await sendEmail({ reg: data?.filtered[x], eventData: eventData, preview: false, html: emailHtml, subject: emailSubject })
            // eslint-disable-next-line no-loop-func -- counters (failed/success) are intentionally shared across iterations
            .then((result) => {
              if (result?.data.error) {
                toast.warning(result?.data.message);
                failed++;
              }
              else {
                success++;
              }
            })
            .catch((e) => {
              // Sentry.captureException(e);
              toast.error(e.message);
            });
        } else {
          incomplete++;
        }
      }
      setSending(2);
      setPreviewEmailHtml("Send complete.<br/>Success: " + success + "<br/>Incomplete: " + incomplete + "<br/>Failed: " + failed + "<br/>Total: " + total + "<br/>");
      // setEmailHtml("");
      // setEmailSubject("");
      setReminderEmailList([]);
      var ele = document.getElementsByName('reminderCheck');
      for (let i = 0; i < ele.length; i++) {
        if (ele[i].type === 'checkbox')
          ele[i].checked = false;
      }
    }
  }
  const sendSMSBody = async () => {
    if (data?.filtered?.length > 0) {
      if (!smsBody) {
        alert('No SMS message found.');
        return;
      }
      let total = reminderEmailList.length;
      let success = 0;
      let incomplete = 0;
      let failed = 0;
      setSending(1);
      setPreviewSMS('Sending ' + 0 + ' of ' + total + ', please wait.');
      for (var i = 0; i < reminderEmailList.length; i++) {
        let x = reminderEmailList[i];
        setPreviewSMS('Sending ' + i + ' of ' + total + ', please wait.');
        if (data?.filtered[x].phone && smsBody) {
          var sendSMS = firebase
            .functions()
            .httpsCallable("appV2/sendSMSBody");
          await sendSMS({ reg: data?.filtered[x], eventData: eventData, preview: false, isGuest: false, toPhone: data?.filtered[x].phone, sms: smsBody })
            // eslint-disable-next-line no-loop-func -- counters (failed/success) are intentionally shared across iterations
            .then((result) => {
              if (result?.data.error) {
                toast.warning(result?.data.message);
                failed++;
              }
              else {
                success++;
              }
            })
            .catch((e) => {
              // Sentry.captureException(e);
              toast.error(e.message);
            });
        } else {
          incomplete++;
        }
      }
      setSending(2);
      setPreviewSMS("Send complete.<br/>Success: " + success + "<br/>Incomplete: " + incomplete + "<br/>Failed: " + failed + "<br/>Total: " + total + "<br/>");
      // setEmailHtml("");
      // setEmailSubject("");
      setReminderEmailList([]);
      var ele = document.getElementsByName('reminderCheck');
      for (let i = 0; i < ele.length; i++) {
        if (ele[i].type === 'checkbox')
          ele[i].checked = false;
      }
    }
  }
  const sendGuestEmail = async () => {
    if (data?.filtered?.length > 0) {
      if (!emailHtml) {
        alert('No email message found.');
        return;
      }
      if (!emailSubject) {
        alert('No email subject found.');
        return;
      }
      let total = reminderEmailList.length;
      let success = 0;
      let incomplete = 0;
      let failed = 0;
      setSending(1);
      setPreviewEmailHtml('Sending ' + 0 + ' of ' + total + ', please wait.');
      for (var i = 0; i < reminderEmailList.length; i++) {
        let x = reminderEmailList[i];
        setPreviewEmailHtml('Sending ' + i + ' of ' + total + ', please wait.');
        if (data?.filtered[x].guestEmail && emailHtml && emailSubject) {
          var sendGuestEmail = firebase
            .functions()
            .httpsCallable("appV2/sendConfirmationEmailToGuests");
          await sendGuestEmail({ reg: data?.filtered[x], eventData: eventData, preview: false, html: emailHtml, subject: emailSubject })
            // eslint-disable-next-line no-loop-func -- counters (failed/success) are intentionally shared across iterations
            .then((result) => {
              if (result?.data.error) {
                toast.warning(result?.data.message);
                failed++;
              }
              else {
                success++;
              }
            })
            .catch((e) => {
              // Sentry.captureException(e);
              toast.error(e.message);
            });
        } else {
          incomplete++;
        }
      }
      setSending(2);
      setPreviewEmailHtml("Send complete.<br/>Success: " + success + "<br/>Incomplete: " + incomplete + "<br/>Failed: " + failed + "<br/>Total: " + total + "<br/>");
      // setEmailHtml("");
      // setEmailSubject("");
      setReminderEmailList([]);
      var ele = document.getElementsByName('reminderCheck');
      for (let i = 0; i < ele.length; i++) {
        if (ele[i].type === 'checkbox')
          ele[i].checked = false;
      }
    }
  }
  const sendGuestSMS = async () => {
    if (data?.filtered?.length > 0) {
      if (!smsBody) {
        alert('No SMS message found.');
        return;
      }
      let total = reminderEmailList.length;
      let success = 0;
      let incomplete = 0;
      let failed = 0;
      setSending(1);
      setPreviewSMS('Sending ' + 0 + ' of ' + total + ', please wait.');
      for (var i = 0; i < reminderEmailList.length; i++) {
        let x = reminderEmailList[i];
        setPreviewSMS('Sending ' + i + ' of ' + total + ', please wait.');
        if (data?.filtered[x].guestPhone && smsBody) {
          var sendGuestSMS = firebase
            .functions()
            .httpsCallable("appV2/sendSMSBody");
          await sendGuestSMS({ reg: data?.filtered[x], eventData: eventData, preview: false, isGuest: true, toPhone: data?.filtered[x].guestPhone, sms: smsBody })
            // eslint-disable-next-line no-loop-func -- counters (failed/success) are intentionally shared across iterations
            .then((result) => {
              if (result?.data.error) {
                toast.warning(result?.data.message);
                failed++;
              }
              else {
                success++;
              }
            })
            .catch((e) => {
              // Sentry.captureException(e);
              toast.error(e.message);
            });
        } else {
          incomplete++;
        }
      }
      setSending(2);
      setPreviewSMS("Send complete.<br/>Success: " + success + "<br/>Incomplete: " + incomplete + "<br/>Failed: " + failed + "<br/>Total: " + total + "<br/>");
      // setEmailHtml("");
      // setEmailSubject("");
      setReminderEmailList([]);
      var ele = document.getElementsByName('reminderCheck');
      for (let i = 0; i < ele.length; i++) {
        if (ele[i].type === 'checkbox')
          ele[i].checked = false;
      }
    }
  }
  const fetch = async (requestedEvent = eventData) => {
    if (!requestedEvent?.id) return;
    const request = ++requestGeneration.current;
    setAllSelected(false); setReminderEmailList([]);
    setData({ isFetching: true, message: null });
    try {
      const snapshot = await db.collection('registrations').where('eventId', '==', requestedEvent.id).get();
      if (request !== requestGeneration.current) return;
      const registrations = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((registration) => {
          if (!requestedEvent.allowPasscodes || !requestedEvent.requireMultiUsePasscodes || user.role === 1 || !registration.passcode) return true;
          const code = requestedEvent.multiUsePasscodes?.find((candidate) => candidate.passcode?.toUpperCase() === registration.passcode.toUpperCase());
          return code && (!code.usersAllowed?.length || code.usersAllowed.some((allowed) => allowed.name === user.email));
        }).map((registration) => ({ ...registration,
          sessionDate: registration.sessionDate?.replace(',', ''),
          createdDate: registration.createdAt ? moment(registration.createdAt.seconds ? registration.createdAt.seconds * 1000 : registration.createdAt).format('MM/DD/YYYY h:mm A') : '',
          updatedDate: registration.updatedAt ? moment(registration.updatedAt.seconds ? registration.updatedAt.seconds * 1000 : registration.updatedAt).format('MM/DD/YYYY h:mm A') : '',
        }));
      setDayList([...new Set(registrations.filter((registration) => registration.sessionId && registration.sessionDate).map((registration) => registration.sessionDate))]
        .sort((a, b) => moment(a, 'dddd MMMM Do YYYY').valueOf() - moment(b, 'dddd MMMM Do YYYY').valueOf()));
      setSessionList([...new Set(registrations.filter((registration) => registration.sessionId && registration.sessionDate).map((registration) => `${registration.sessionDate} ${registration.sessionTime || ''}`))]
        .sort((a, b) => moment(a, 'dddd MMMM Do YYYY h:mm A').valueOf() - moment(b, 'dddd MMMM Do YYYY h:mm A').valueOf()));
      filter({ registrations, message: null });
    } catch (error) {
      if (request === requestGeneration.current) setData({ isFetching: false, message: 'Unable to load registrations. Refresh to try again.' });
    }
  };
  const getRegCount = async (eventId) => {
    let regInfo = await firebase.functions().httpsCallable("appV2/regCount");
    let result = await regInfo({ id: eventId ? eventId : eventData?.id });
    setRegCount(result.data);
  }
  const getItemCapacity = async (eventId, item, index) => {
    let itemInfo = await firebase.functions().httpsCallable("appV2/regItemCount");
    let result = await itemInfo({ id: eventId, fieldName: item.fieldName });
    setRegItemCounts((regItemCounts) => [...regItemCounts, { sortOrder: index, 'item': item.fieldLabel, 'regItemCount': result.data, 'fieldCapacityNum': item.fieldCapacityNum }]);
  }
  const updateItemCapacity = async (capacityEvent = eventData) => {
    let i = 0;
    // await capacityEvent?.additionalFields.map(async (item, index) => {
    if (capacityEvent?.additionalFields) {
      capacityEvent?.additionalFields.forEach((item) => {
        if (item.fieldType === "CommerceItem") {
          if (item.fieldCapacity === "itemCapacity") {
            getItemCapacity(capacityEvent?.id, item, i);
            i++;
          }
        }
      });
    }
    // tmpRegItemCounts.sort((a, b) => (a.sortOrder > b.sortOrder) ? 1 : -1);
    // setRegItemCounts(tmpRegItemCounts);
  }
  const loadInit = async () => {
    if (!id) { history('/manage', { replace: true }); return; }
    const snapshot = await db.collection('events').doc(id).get();
    const tmpEventData = snapshot.exists ? { ...snapshot.data(), id: snapshot.id } : null;
    if (!tmpEventData) { history('/manage', { replace: true }); return; }
    setEventData(tmpEventData); setCurrentEventData(tmpEventData);
    sessionStorage.setItem('eventData', JSON.stringify(tmpEventData));
    let tmpFieldList = { ...DEFAULT_FIELDS,
      itemInfo: !!(tmpEventData.allowCapacity && tmpEventData.capacityType === 'session' && tmpEventData.sessionItems?.length > 1),
      sessionInfo: !!(tmpEventData.allowCapacity && tmpEventData.capacityType === 'session') };
    let tmpAvailableFieldList = [...BASE_AVAILABLE_FIELDS].filter((field) =>
      !['guest', 'guestEmail', 'waiver', 'guestWaiver'].includes(field));
    setCommItem(false);
    const addAvailableField = (fieldName) => {
      if (tmpAvailableFieldList.indexOf(fieldName) === -1) {
        tmpAvailableFieldList.push(fieldName);
      }
    };
    if (tmpEventData?.allowGuests) {
      addAvailableField("guest");
      addAvailableField("guestEmail");
    }
    if (tmpEventData?.allowWaivers)
      addAvailableField("waiver");
    if (tmpEventData?.allowGuestWaivers)
      addAvailableField("guestWaiver");
    if (tmpEventData?.id) {
      // let tempReg = JSON.parse(localStorage.getItem('regData'));
      let tempReg = null;
      if (!tempReg || tempReg.length === 0) {
        tempReg = [];
      }
      setData({
        isFetching: tempReg.length === 0,
        message: null,
        filter: tempReg.length > 0,
      });
      updateItemCapacity(tmpEventData);
      if (tmpEventData?.additionalFields) {
        let isComm = false;
        tmpEventData?.additionalFields.forEach((item, index) => {
          tmpFieldList[item.fieldName] = false;
          addAvailableField(item.fieldName);
          if (item.fieldType === "CommerceItem") {
            tmpFieldList[item.fieldName + "Cost"] = false;
            addAvailableField(item.fieldName + "Cost");
            if (!isComm) {
              isComm = true;
              setCommItem(isComm);
            }
          }
        });
        if (isComm) {
          tmpFieldList["subCost"] = false;
          tmpFieldList["paymentStatus"] = false;
          tmpFieldList["taxCost"] = false;
          tmpFieldList["totalCost"] = false;
          tmpFieldList["adyenRef"] = false;
          tmpFieldList["adyenLink"] = false;
          if (tmpEventData?.paymentTax) {
            addAvailableField("subCost");
            addAvailableField("taxCost");
          }
          addAvailableField("totalCost");
          addAvailableField("adyenRef");
          addAvailableField("adyenLink");
        }
      }
      let storedFieldList = readRegistrationsTableSetting(tmpEventData.id, "fieldList", null);
      let newFieldList = { ...tmpFieldList };
      Object.keys(tmpFieldList).forEach((key) => {
        if (typeof storedFieldList?.[key] === 'boolean') newFieldList[key] = storedFieldList[key];
      });
      setFieldList(newFieldList);
      setAvailableFieldList(tmpAvailableFieldList);
      getRegCount(tmpEventData.id);
      const storedFilters = normalizeRegFilters(
        readRegistrationsTableSetting(tmpEventData.id, "regFilters", DEFAULT_REG_FILTERS)
      );
      const storedSortOrder = normalizeSortOrder(readRegistrationsTableSetting(tmpEventData.id, "sortOrder", DEFAULT_SORT_ORDER));
      setShowFieldList(false);
      settingsRef.current = { regFilters: storedFilters, sortOrder: storedSortOrder };
      setRegFilters(storedFilters);
      setSortOrder(storedSortOrder);
      fetch(tmpEventData, storedSortOrder);
    }
  };
  useEffect(() => {
    db.collection("dealers").get().then((querySnapshot) => {
      if (querySnapshot.docs.length > 0) {
        let tmpDealers = [];
        querySnapshot.forEach((doc) => {
          let tmpDealer = doc.data();
          tmpDealer.id = doc.id;
          tmpDealers.push(tmpDealer);
        });
        setDealers(tmpDealers);
      }
    });
    loadInit().catch(() => setData({ isFetching: false, message: "Unable to load event. Return to the dashboard and try again." }));
    DOMPurify.setConfig({ ADD_ATTR: ['target'] });
    return () => { requestGeneration.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- each event is mounted independently
  }, []);
  useEffect(() => {
    
    // if (!data.isFetching && data.filter) {
    //   filter(data);
    // }
  }, [data]);
  useEffect(() => {
    setAllSelected(false);
    setReminderEmailList([]);
    var ele = document.getElementsByName('reminderCheck');
    for (var i = 0; i < ele.length; i++) {
      if (ele[i].type === 'checkbox')
        ele[i].checked = false;
    }
    if (eventData?.id) setStorageUnavailable(!writeRegistrationsTableSetting(eventData.id, "regFilters", normalizeRegFilters(regFilters)));
    if (!data.isFetching) filter(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intent: re-run only on regFilters change; data and filter() are component-scope and read at call time
  }, [regFilters]);
  useEffect(() => {
    setAllSelected(false); setReminderEmailList([]);
    if (eventData?.id) setStorageUnavailable(!writeRegistrationsTableSetting(eventData.id, "sortOrder", sortOrder));
    if (!data.isFetching) filter(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intent: refetch only on sortOrder change; data.isFetching/eventData/fetch captured intentionally to avoid spurious refetches
  }, [sortOrder]);
  useEffect(() => {
    if (!data.isFetching && currentPageIndex !== regFilters.pageIndex) {
      setRegFilters((filters) => ({ ...filters, pageIndex: currentPageIndex }));
    }
  }, [currentPageIndex, regFilters.pageIndex, data.isFetching]);
  useEffect(() => {
    if (!showFieldList) return;
    const dismiss = (event) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !fieldPicker.current?.contains(event.target)) setShowFieldList(false);
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', dismiss); };
  }, [showFieldList]);
  function Fetching(props) {
    if (data.isFetching) {
      return <div className="rounded-md bg-zinc-100 p-3 text-sm text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300" role="status">Loading registrations…</div>;
    } else {
      return null;
    }
  }
  function Message(props) {
    if (data.message) {
      return <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-900">{data.message}</div>;
    } else {
      return null;
    }
  }
  const deleteFile = (pathToFile, fileName) => {
    const ref = firebase.storage().ref(pathToFile);
    const childRef = ref.child(fileName);
    if (childRef) childRef.delete();
  };
  const downloadAllWaivers = async () => {
    if (user.role === 1 || user.role === 2) {
      var zip = new JSZip();
      var zipFilename = "waivers_" + eventData?.id + ".zip";
      let total = data.registrations.length;
      let countReg = 0;
      data.registrations.map(async (reg) => {
        try {
          let url = await storage
            .child("/waivers/" + reg?.id + ".pdf")
            .getDownloadURL();
          let file = await JSZipUtils.getBinaryContent(url);
          countReg = countReg + 1;
          zip.file(reg?.id + ".pdf", file, { binary: true });
          if (countReg === total) {
            let content = await zip.generateAsync({ type: "blob" });
            saveAs(content, zipFilename);
          }
        } catch (e) {
          total = total - 1;
        }
      });
    } else {
      alert("Only an admin/manager can download all waivers.");
    }
  };
  const downloadAllGuestWaivers = async () => {
    if (user.role === 1 || user.role === 2) {
      var zip = new JSZip();
      var zipFilename = "guest_waivers_" + eventData?.id + ".zip";
      let total = data.registrations.length;
      let countReg = 0;
      data.registrations.map(async (reg) => {
        try {
          let url = await storage
            .child("/guest_waivers/" + reg?.id + ".pdf")
            .getDownloadURL();
          let file = await JSZipUtils.getBinaryContent(url);
          countReg = countReg + 1;
          zip.file(reg?.id + ".pdf", file, { binary: true });
          if (countReg === total) {
            let content = await zip.generateAsync({ type: "blob" });
            saveAs(content, zipFilename);
          }
        } catch (e) {
          total = total - 1;
        }
      });
    } else {
      alert("Only an admin/manager can download all guest waivers.");
    }
  };
  const getExperience = (value) => {
    switch (value) {
      case "A1": return "I’ve never driven a Porsche, and not familiar with the Brand";
      case "A2": return "I’ve never driven a Porsche, but I’m familiar with the Brand";
      case "A3": return "I’ve driven a Porsche, but never owned a Porsche";
      case "A4": return "I’ve owned a Porsche, but do not currently own";
      case "A5": return "I currently own a Porsche";
      default: return value;
    }
  };
  const getModelInterest = (value) =>
    getModelInterestLabel(value, modelInterestCatalog);
  const getPurchaseTimeFrame = (value) => {
    switch (value) {
      case "id_ao1_timeframe": return "0 - 3 months";
      case "id_ao2_timeframe": return "3 - 6 months";
      case "id_ao3_timeframe": return "6 - 9 months";
      case "id_ao4_timeframe": return "9 - 12 months";
      case "id_ao5_timeframe": return "12 months+";
      default: return value;
    }
  }
  const downloadSubmissions = async (filtered) => {
    let tmpAllSubmissions = [];
    let tmpData = filtered ? data.filtered : data.registrations;
    tmpData.map(async (reg, i) => {
      let tmpSubmission = {};
      tmpSubmission["id"] = reg["id"];
      if (eventData?.dataFields["company"]?.visible || eventData?.dataFields["company"]?.adminOnly)
        tmpSubmission["company"] = reg["company"];
      if (eventData?.dataFields["title"]?.visible || eventData?.dataFields["title"]?.adminOnly)
        tmpSubmission["title"] = reg["title"];
      if (eventData?.dataFields["lastName"]?.visible || eventData?.dataFields["lastName"]?.adminOnly)
        tmpSubmission["lastName"] = reg["lastName"];
      if (eventData?.dataFields["firstName"]?.visible || eventData?.dataFields["firstName"]?.adminOnly)
        tmpSubmission["firstName"] = reg["firstName"];
      if (eventData?.dataFields["email"]?.visible || eventData?.dataFields["email"]?.adminOnly)
        tmpSubmission["email"] = reg["email"];
      if (eventData?.dataFields["phone"]?.visible || eventData?.dataFields["phone"]?.adminOnly)
        tmpSubmission["phone"] = reg["phone"];
      if (eventData?.dataFields["address1"]?.visible || eventData?.dataFields["address1"]?.adminOnly)
        tmpSubmission["address1"] = reg["address1"];
      if (eventData?.dataFields["address2"]?.visible || eventData?.dataFields["address2"]?.adminOnly)
        tmpSubmission["address2"] = reg["address2"];
      if (eventData?.dataFields["city"]?.visible || eventData?.dataFields["city"]?.adminOnly)
        tmpSubmission["city"] = reg["city"];
      if (eventData?.dataFields["state"]?.visible || eventData?.dataFields["state"]?.adminOnly)
        tmpSubmission["state"] = reg["state"];
      if (eventData?.dataFields["postal"]?.visible || eventData?.dataFields["postal"]?.adminOnly)
        tmpSubmission["postal"] = reg["postal"];
      if (eventData?.allowPushToCap) {
        tmpSubmission["dealer_optin"] = reg["dealer_optin"] ? "true" : "false";
        tmpSubmission["sentToC@P"] = reg["leadSent"] ? "true" : "false";
      }
      if (eventData?.dataFields["dealer"]?.visible || eventData?.dataFields["dealer"]?.adminOnly)
        tmpSubmission["dealer"] = reg["dealer"];
      if (eventData?.dataFields["experience"]?.visible || eventData?.allowPushToCap  || eventData?.dataFields["experience"]?.adminOnly)
        tmpSubmission["experience"] = getExperience(reg["experience"]);
      if (eventData?.dataFields["modelInterest"]?.visible || eventData?.allowPushToCap  || eventData?.dataFields["modelInterest"]?.adminOnly)
        tmpSubmission["modelInterest"] = getModelInterest(reg["modelInterest"]);
      if (eventData?.dataFields["purchaseTimeFrame"]?.visible || eventData?.allowPushToCap  || eventData?.dataFields["purchaseTimeFrame"]?.adminOnly)
        tmpSubmission["purchaseTimeFrame"] = getPurchaseTimeFrame(reg["purchaseTimeFrame"]);
      let showPayment = false;
      for (const key2 in eventData?.additionalFields) {
        if (eventData?.additionalFields[key2].fieldType === "CommerceItem")
          showPayment = true;
        let tmpVal = reg[eventData?.additionalFields[key2].fieldName];
        if (typeof tmpVal === 'string' || tmpVal instanceof String) {
          tmpVal = tmpVal.replaceAll('"', '');
          tmpVal = tmpVal.replaceAll(',', '');
        }
        if (eventData?.additionalFields[key2].fieldName) {
          tmpSubmission[eventData?.additionalFields[key2].fieldName] = tmpVal;
          if (eventData?.additionalFields[key2].fieldType === "CommerceItem")
            tmpSubmission[eventData?.additionalFields[key2].fieldName + "Cost"] = reg[eventData?.additionalFields[key2].fieldName + "Cost"];
        }
      }
      // let tmpSubmission = {
      //   id: reg.id,
      //   firstName: reg.firstName,
      //   lastName: reg.lastName,
      //   email: reg.email,
      //   phone: reg.phone,
      //   status: reg.status,
      // };
      if (eventData?.allowCapacity && eventData?.capacityType === "session") {
        tmpSubmission["sessionDate"] = reg["sessionDate"];
        tmpSubmission["sessionTime"] = reg["sessionTime"];
        tmpSubmission["sessionItem"] = getSessionItem(reg["sessionItem"]);
      }
      if (eventData?.allowPasscodes) {
        tmpSubmission["passcode"] = reg["passcode"] + getPasscodeDescription(reg["passcode"]);
      }
      if (eventData?.allowUpload) {
        var storageRef = firebase.storage().ref("/submissions/" + reg.id + "/");
        let k = 1;
        let allFiles = await storageRef.listAll();
        allFiles.items.forEach(async (fileRef) => {
          let fileUrl = await fileRef.getDownloadURL();
          tmpSubmission["file" + k] = fileUrl;
          k++;
        });
      }
      //Guest?
      if (eventData?.allowWaivers) {
        tmpSubmission["hasWaiver"] = reg["hasWaiver"];
      }
      if (eventData?.allowGuestWaivers) {
        tmpSubmission["hasGuestWaiver"] = reg["hasGuestWaiver"];
      }
      if (showPayment) {
        if (eventData?.paymentTax) {
          tmpSubmission["subCost"] = reg["subCost"];
          tmpSubmission["taxCost"] = reg["taxCost"];
        }
        tmpSubmission["totalCost"] = reg["totalCost"];
        tmpSubmission["adyenPaymentStatus"] = reg["adyenPaymentStatus"];
        tmpSubmission["adyenRef"] = reg["adyenRef"];
        tmpSubmission["adyenRefDate"] = reg["adyenRefDate"];
        tmpSubmission["adyenResultCode"] = reg["adyenResultCode"];
        tmpSubmission["adyenRefusalReason"] = reg["adyenRefusalReason"];
        tmpSubmission["adyenLink"] = reg["adyenLink"];
        tmpSubmission["adyenLinkId"] = reg["adyenLinkId"];
        tmpSubmission["adyenLinkExpire"] = reg["adyenLinkExpire"];
      }
      tmpSubmission["status"] = getRegistrationStatusLabel(reg);
      tmpSubmission["notes"] = reg["notes"];
      try {
        tmpSubmission["createdAt"] = reg["createdAt"] ? moment(reg["createdAt"].toDate()).format("MM/DD/YYYY h:mm:ss A") : "";
      } catch {
        tmpSubmission["createdAt"] = "N/A";
      }
      try {
        tmpSubmission["updatedAt"] = reg["updatedAt"] ? moment(reg["updatedAt"].toDate()).format("MM/DD/YYYY h:mm:ss A") : "";
      } catch {
        tmpSubmission["updatedAt"] = "N/A";
      }
      tmpAllSubmissions.push(tmpSubmission);
      console.log(tmpAllSubmissions);
      setAllSubmissions(tmpAllSubmissions);
    });
  };
  const clearLeadData = async () => {
    if (user.role === 1) {
      alert(
        "Clearing processed lead indicator.  This may take several minutes.  Do not restart until you see the success message."
      );
      data.registrations.forEach((reg, i) => {
        if (reg.leadSent) {
          reg.leadSent = false;
          db.collection("registrations")
            .doc(reg.id)
            .update({ leadSent: false });
        }
      });
      alert(
        "Success. Leads will be resent during nightly lead processing job."
      );
    } else {
      alert("Only an admin can delete all registrations.");
    }
  };
  const deleteAllRegistrations = () => {
    if (user.role === 1) {
      alert(
        "Registgrations are being deleted.  This may take several minutes."
      );
      data.registrations.forEach((reg, i) => {
        if(eventData?.allowWaivers) {
        try {
          deleteFile("/waivers/", reg.id + ".pdf");
          deleteFile("/waivers/", reg.id + ".jpg");
        } catch {
          console.log('error deleting files');
        }
        
        try {
          deleteFile("/guest_waivers/", reg.id + ".pdf");
          deleteFile("/guest_waivers/", reg.id + ".jpg");
        }
        catch {
          console.log('error deleting files');
        
        }
      }
        db.collection("registrations")
          .doc(reg.id)
          .delete();
      });
      // fetch()
      history("/manage/");
    } else {
      alert("Only an admin can delete all registrations.");
    }
  };
  const handleFileRead = async (event) => {
    let valid = 0;
    let invalid = 0;
    const file = event.target.files[0];
    if (file.name.indexOf(".csv") === -1) {
      alert(
        "File must be a valid csv file, download the provided template if needed."
      );
      return;
    }
    const csv = await processsFile(file);
    var allTextLines = csv.split(/\r\n|\n/);
    var lines = [];
    for (var i = 0; i < allTextLines.length; i++) {
      var data = allTextLines[i].split(",");
      var tarr = [];
      if (data.length === 12 && i > 0) {
        valid = valid + 1;
        for (var j = 0; j < data.length; j++) {
          tarr.push(data[j]);
        }
        lines.push(tarr);
      } else if (data.length === 1 && data[0] === "") {
      } else if (i > 0) {
        invalid = invalid + 1;
      }
    }
    console.log(lines);
    lines.map(async (l, i) => {
      let r = {};
      r.id = db.collection("registrations").doc().id;
      r.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      r.status = "Registered";
      r.eventId = eventData?.id;
      r.company = l[0];
      r.title = l[1];
      r.firstName = l[2];
      r.lastName = l[3];
      r.email = l[4];
      r.phone = l[5];
      r.address1 = l[6];
      r.address2 = l[7];
      r.city = l[8];
      r.state = l[9];
      r.postal = l[10];
      r.dealer = l[11];
      // r.packages = l[12];
      if(r.postal?.length === 4) r.postal = "0" + r.postal;
      if(r.postal && r.email) {
      await db.collection("registrations")
        .doc(r.id)
        .set(r);
      }
    });
    
    toast.success(
      "Import complete: " + valid ? valid : 0 + " of " + (valid + invalid) + " records."
    );
      fetch(eventData);
    setImportReg(false);
  };
  const processsFile = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
      fileReader.readAsText(file);
    });
  };
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intent: re-check auth only on user change; checkAuth is component-scope and references current user via closure
  }, [user]);
  const checkAuth = () => {
    if (!user.isAuthenticated) {
      history("/manage/sign-in");
      return false;
    } else {
      return true;
    }
  };
  const alertCheck = (x, r) => {
    let alert = false;
    switch (x) {
      case "IncompleteWaiver":
        if (r.status !== "Waitlist" && r.status !== "Cancelled" && !r.hasWaiver)
          alert = true;
        break;
      case "IncompleteGuestWaiver":
        if (r.status !== "Waitlist" && r.status !== "Cancelled" && !r.hasGuestWaiver)
          alert = true;
        break;
      case "IncompletePayment":
        if (r.status !== "Waitlist" && r.status !== "Cancelled" && (r.adyenPaymentStatus === "Pending" || r.adyenPaymentStatus === "" || !r.adyenPaymentStatus))
          alert = true;
        break;
      case "IsGuest":
        if (r.isGuest)
          alert = true;
        break;
      case "Pending":
        if (r.status === "Pending")
          alert = true;
        break;
      case "NotCheckedIn":
        if (r.status === "Registered" || r.status === "Pending")
          alert = true;
        break;
      case "CheckedIn":
        if (r.status === "Checked-In")
          alert = true;
        break;
      case "Completed":
        if (r.status === "Completed")
          alert = true;
        break;
      case "Waitlist":
        if (r.status === "Waitlist")
          alert = true;
        break;
      default:
        alert = false;
    }
    return alert;
  }
  const alertRules = (r) => {
    if (alertCheck(eventData?.redAlert, r)) {
      return "red-alert ";
    } else if (alertCheck(eventData?.yellowAlert, r)) {
      return "yellow-alert ";
    } else if (alertCheck(eventData?.orangeAlert, r)) {
      return "orange-alert ";
    } else if (alertCheck(eventData?.purpleAlert, r)) {
      return "purple-alert ";
    } else if (alertCheck(eventData?.blueAlert, r)) {
      return "blue-alert ";
    } else if (alertCheck(eventData?.greenAlert, r)) {
      return "green-alert ";
    }
    return "";
  }
  const clearReminder = () => {
    setCreateReminder(false);
    setCreateReminderSMS(false);
    setSending(0);
    setPreviewEmailHtml("");
    setPreviewSMS("");
    setEmailHtml("");
    setSMSBody("");
    setEmailSubject("");
    setReminderEmailList([]);
    var ele = document.getElementsByName('reminderCheck');
    for (var i = 0; i < ele.length; i++) {
      if (ele[i].type === 'checkbox')
        ele[i].checked = false;
    }
  }
  const handleScan = (d) => {
    let d_arr = d.split("/");
    let regId = d_arr[d_arr.length - 1];
    history("/manage/registration/" + regId);
  };
  const handleScanError = (e) => {
    // setProgress("...error reading badge...");
    console.log(e);
  };
  const labelCls = "block text-sm font-medium text-zinc-700 dark:text-zinc-300";
  const inputCls = "mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white";
  const primaryBtnCls = "inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100";
  const secondaryBtnCls = "inline-flex items-center justify-center rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700";
  const linkBtnCls = "inline-flex items-center text-sm font-medium text-zinc-700 underline-offset-2 hover:text-zinc-900 hover:underline dark:text-zinc-300 dark:hover:text-zinc-100";
  const formGroupCls = "mb-4";
  return (
    <div className="registration-list mx-auto w-full px-0 py-4 sm:px-4 sm:py-6">
      <BarcodeReader onError={handleScanError} onScan={handleScan} />
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">{eventData?.sysname ? eventData?.sysname : eventData?.name}</h1>
      <div className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
        <label>Registrations:</label>{" "}
        <span className="fs-md">
          {data.filtered?.length ? data.filtered?.length : 0} of {data.registrations?.length}
        </span>
      </div>
      {((eventData?.allowCapacity && eventData?.capacityType !== "session")) &&
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-zinc-700 dark:text-zinc-300">
          <div className={regCount >= eventData?.capacity ? "w-full md:flex-1 red-alert rounded-md px-3 py-2" : "w-full md:flex-1 rounded-md px-3 py-2"}><label>Capacity:</label> {regCount} {eventData?.allowCapacity ? "of " + +eventData?.capacity : ""}</div>
          {[].concat(regItemCounts).sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1).map((r, i) => (
            <div className={r.regItemCount >= r.fieldCapacityNum ? "w-full md:flex-1 red-alert rounded-md px-3 py-2" : "w-full md:flex-1 rounded-md px-3 py-2"} key={i}><label>{r.item}:</label> {r.regItemCount} of {r.fieldCapacityNum}</div>
          ))}</div>
      }
      <div className="py-2"></div>
      <div className="flex flex-wrap gap-4">

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {!createReminder && !importReg && !createReminderSMS && (
              <button
                onClick={() => {
                  setCreateReminder(true);
                }}
                className={primaryBtnCls}
              >
                Create Email
              </button>
            )}
            {!createReminder && !importReg && !createReminderSMS && (
              <button
                onClick={() => {
                  setCreateReminderSMS(true);
                }}
                className={primaryBtnCls}
              >
                Create SMS
              </button>
            )}
            {!createReminder && !importReg && !createReminderSMS && (
              <button
                onClick={() => {
                  setImportReg(true);
                }}
                className={secondaryBtnCls}
              >
                Import List
              </button>
            )}
          </div>
          {createReminder && (
            <div>      <div className="pb-3">
              {previewEmailHtml && sending !== 1 &&
                <button className={linkBtnCls} onClick={() => { setPreviewEmailHtml(null); }}>
                  Back
                </button>}
              {!previewEmailHtml && <>
                <div className={formGroupCls}>
                  <label className={labelCls} htmlFor="emailTemplate">Email Templates</label>
                  <select id="emailTemplate"
                    className={inputCls}
                    name="emailTemplate"
                    value={emailTemplate}
                    onChange={(e) => {
                      let emailTemplate = e.target.value;
                      setEmailTemplate(emailTemplate);
                      if (emailTemplate === "Pending") {
                        setEmailSubject(eventData?.pendingEmailSubject);
                        setEmailHtml(eventData?.pendingEmailCopy);
                      } else if (emailTemplate === "Confirmation") {
                        setEmailSubject(eventData?.confirmationEmailSubject);
                        setEmailHtml(eventData?.confirmationEmailCopy);
                      } else if (emailTemplate === "Waitlist") {
                        setEmailSubject(eventData?.waitlistEmailSubject);
                        setEmailHtml(eventData?.waitlistEmailCopy);
                      } else if (emailTemplate === "Reminder") {
                        setEmailSubject(eventData?.reminderEmailSubject);
                        setEmailHtml(eventData?.reminderEmailCopy);
                      } else if (emailTemplate === "Cancellation") {
                        setEmailSubject(eventData?.cancellationEmailSubject);
                        setEmailHtml(eventData?.cancellationEmailCopy);
                      } else {
                        let tmpTemplate = eventData?.emailTemplates?.find(o => o.name === emailTemplate);
                        setEmailSubject(tmpTemplate?.subject);
                        setEmailHtml(tmpTemplate?.html);
                      }
                    }}
                  >
                    <option value=""></option>
                    <option value="Pending">Pending Email</option>
                    <option value="Confirmation">Confirmation Email</option>
                    <option value="Waitlist">Waitlist Email</option>
                    <option value="Reminder">Reminder Email</option>
                    <option value="Cancellation">Cancellation Email</option>
                    {eventData?.emailTemplates && eventData?.emailTemplates.map((et, i) => (
                      <option key={i} value={et.name}>{et.name}</option>
                    ))}
                  </select>
                </div>
                <div className={formGroupCls}>
                  <label
                    className={labelCls}>
                    Template Name
                  </label>
                  <input
                    type="text"
                    name="emailTemplate"
                    value={emailTemplate}
                    onChange={(e) => { setEmailTemplate(e.target.value) }}
                    className={inputCls}
                  />
                </div>
                <div className={formGroupCls}>
                  <label
                    className={labelCls}>
                    Ad Hoc Email Subject
                  </label>
                  <input
                    type="text"
                    id="emailSubject"
                    name="emailSubject"
                    value={emailSubject}
                    onChange={(e) => { setEmailSubject(e.target.value) }}
                    className={inputCls}
                  />
                </div>
                <JoditEditor
                  ref={editor}
                  config={{
                    readonly: false,
                    toolbar: true
                  }}
                  value={emailHtml}
                  // config={configJotit}
                  tabIndex={1} // tabIndex of textarea
                  onBlur={newContent => setEmailHtml(newContent)} // preferred to use only this option to update the content for performance reasons
                // onChange={newContent => setEmailHtml(newContent)}
                />
                <button className={primaryBtnCls} onClick={() =>
                  window.confirm(
                    "Are you sure you want to send this email to " + reminderEmailList.length + " recipients?"
                  )
                    ? sendEmail()
                    : console.log("action cancelled by user.")
                }>
                  Send Email
                </button>&nbsp;&nbsp;
                {eventData?.allowGuests && <>
                <button className={primaryBtnCls} onClick={() =>
                  window.confirm(
                    "Are you sure you want to send this email to the guests of these " + reminderEmailList.length + " recipients?"
                  )
                    ? sendGuestEmail()
                    : console.log("action cancelled by user.")
                }>
                  Send Email to Guests
                </button>&nbsp;&nbsp;
</>}
                <button className={primaryBtnCls} onClick={() =>
                  window.confirm(
                    "Are you sure you want to download pdfs for " + reminderEmailList.length + " recipients?"
                  )
                    ? downloadPdfDocuments()
                    : console.log("action cancelled by user.")
                }>
                  Download Archive
                </button>
                <div className="py-2"></div>
                <button className={linkBtnCls} onClick={previewEmail}>
                  Preview Email
                </button>&nbsp;&nbsp;
                <button className={linkBtnCls} onClick={async () => {
                  if (!emailTemplate || emailTemplate === "") {
                    alert("Please select an email template.");
                    return;
                  }
                  if (!emailSubject || emailSubject === "") {
                    alert("Please enter an email subject.");
                    return;
                  }
                  if (!emailHtml || emailHtml === "") {
                    alert("Please enter an email body.");
                    return;
                  }
                  if (emailTemplate === "Pending" || emailTemplate === "Confirmation" || emailTemplate === "Waitlist" || emailTemplate === "Reminder" || emailTemplate === "Cancellation") {
                    alert("You cannot save over a system email template.");
                    return;
                  }
                  let tmpEventData = eventData;
                  if (!tmpEventData?.emailTemplates) {
                    tmpEventData.emailTemplates = [];
                  }
                  if (tmpEventData?.emailTemplates?.find(o => o.name === emailTemplate)) {
                    tmpEventData.emailTemplates = tmpEventData.emailTemplates.filter(o => o.name !== emailTemplate);
                  }
                  tmpEventData.updatedAt = firebase.firestore.FieldValue.serverTimestamp();
                  tmpEventData.emailTemplates.push({ name: emailTemplate, subject: emailSubject, html: emailHtml });
                  await db.collection("events").doc(tmpEventData?.id).set(tmpEventData);
                  console.log(tmpEventData);
                  setEventData(tmpEventData)
                  alert('Email template saved.');
                }}>
                  Save Template
                </button>&nbsp;&nbsp;
                <button className={secondaryBtnCls} onClick={() =>
                  window.confirm(
                    "Are you sure you want to cancel this email?"
                  )
                    ? clearReminder()
                    : console.log("action cancelled by user.")
                }>
                  Cancel
                </button>
              </>
              }
              <div className={previewEmailHtml ? "py-5" : "hidden"}>
                {/* <div className="pb-2"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      previewEmailHtml
                    ),
                  }}
                ></div> */}
                <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Email {sending === 2 ? "Results" : "Preview"}</h4>
                <iframe className="previewEmail" srcDoc={previewEmailHtml} sandbox="allow-same-origin" id="htmlPreviewFrame" name="htmlPreviewFrame" title="Email preview" />
              </div>
            </div></div>
          )}
          {createReminderSMS && (
            <div>      <div className="pb-3">
              {previewSMS && sending !== 1 &&
                <button className={linkBtnCls} onClick={() => { setPreviewSMS(null); }}>
                  Back
                </button>}
              {!previewSMS && <>
                <div className={formGroupCls}>
                  <label className={labelCls} htmlFor="smsTemplate">SMS Templates</label>
                  <select id="smsTemplate"
                    className={inputCls}
                    name="smsTemplate"
                    value={smsTemplate}
                    onChange={(e) => {
                      let smsTemplate = e.target.value;
                      setSMSTemplate(smsTemplate);
                      alert(smsTemplate);
                      let tmpTemplate = eventData?.smsTemplates?.find(o => o.name === smsTemplate);
                      setSMSBody(tmpTemplate?.smsBody);
                    }}
                  >
                    <option value=""></option>
                    {eventData?.smsTemplates && eventData?.smsTemplates.map((et, i) => (
                      <option key={i} value={et.name}>{et.name}</option>
                    ))}
                  </select>
                </div>
                <div className={formGroupCls}>
                  <label
                    className={labelCls}>
                    Template Name
                  </label>
                  <input
                    type="text"
                    name="smsTemplate"
                    value={smsTemplate}
                    onChange={(e) => { setSMSTemplate(e.target.value) }}
                    className={inputCls}
                  />
                </div>
                <div className={formGroupCls}>
                  <label
                    className={labelCls}>
                    SMS Body
                  </label>
                  <textarea
                    type="text"
                    id="smsBody"
                    name="smsBody"
                    value={smsBody}
                    onChange={(e) => { setSMSBody(e.target.value) }}
                    className={inputCls}
                  />
                </div>
                <button className={primaryBtnCls} onClick={() =>
                  window.confirm(
                    "Are you sure you want to send this sms message to " + reminderEmailList.length + " recipients?"
                  )
                    ? sendSMSBody()
                    : console.log("action cancelled by user.")
                }>
                  Send SMS
                </button>&nbsp;&nbsp;
                {eventData?.allowGuests && <>
                <button className={primaryBtnCls} onClick={() =>
                  window.confirm(
                    "Are you sure you want to send this sms to the guests of these " + reminderEmailList.length + " recipients?"
                  )
                    ? sendGuestSMS()
                    : console.log("action cancelled by user.")
                }>
                  Send SMS to Guests
                </button>&nbsp;&nbsp;
                </>}
                <div className="py-2"></div>
                <button className={linkBtnCls} onClick={previewSMS}>
                  Preview SMS
                </button>&nbsp;&nbsp;
                <button className={linkBtnCls} onClick={async () => {
                  if (!smsTemplate || smsTemplate === "") {
                    alert("Please select an sms template.");
                    return;
                  }
                  if (!smsBody || smsBody === "") {
                    alert("Please enter an sms body.");
                    return;
                  }
                  let tmpEventData = eventData;
                  if (!tmpEventData?.smsTemplates) {
                    tmpEventData.smsTemplates = [];
                  }
                  if (tmpEventData?.smsTemplates?.find(o => o.name === smsTemplate)) {
                    tmpEventData.smsTemplates = tmpEventData.smsTemplates.filter(o => o.name !== smsTemplate);
                  }
                  tmpEventData.updatedAt = firebase.firestore.FieldValue.serverTimestamp();
                  tmpEventData.smsTemplates.push({ name: smsTemplate, smsBody: smsBody });
                  await db.collection("events").doc(tmpEventData?.id).set(tmpEventData);
                  alert('SMS template saved.');
                }}>
                  Save Template
                </button>&nbsp;&nbsp;
                <button className={secondaryBtnCls} onClick={() =>
                  window.confirm(
                    "Are you sure you want to cancel this sms?"
                  )
                    ? clearReminder()
                    : console.log("action cancelled by user.")
                }>
                  Cancel
                </button>
              </>
              }
              <div className={previewSMS ? "py-5" : "hidden"}>
                {/* <div className="pb-2"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      previewEmailHtml
                    ),
                  }}
                ></div> */}
                <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">SMS {sending === 2 ? "Results" : "Preview"}</h4>
                <iframe className="previewSMS" srcDoc={previewSMS} sandbox="allow-same-origin" id="htmlPreviewFrame" name="htmlPreviewFrame" title="SMS preview" />
              </div>
            </div></div>
          )}
          {importReg && (
            <>
              <div className={formGroupCls}>
                <label className={labelCls}>Import List</label>
                <div className="mt-2">
                  <input
                    id="originalFileName"
                    type="file"
                    name="originalFileName"
                    onChange={(e) => handleFileRead(e)}
                    className="block text-sm text-zinc-700 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-900 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-zinc-50 hover:file:bg-zinc-800 dark:text-zinc-300 dark:file:bg-white dark:file:text-zinc-900 dark:hover:file:bg-zinc-100"
                  />
                </div>
              </div>
              <div>
                <a href="/assets/import-template.csv" className="text-sm text-zinc-700 underline hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100">
                  Download Import Template
                </a>
              </div>
              <div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setImportReg(false);
                  }}
                  className="text-sm text-zinc-700 underline hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
        <div className="flex-1 min-w-0 text-right">
          <button
            onClick={() => history("/manage/registration/new/" + eventData?.id)}
            className={primaryBtnCls}
          >
            Add Registration
          </button>
        </div>
      </div>
      <div className="py-2"></div>
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[16rem]">
          <div className={formGroupCls}>
            <label className={labelCls} htmlFor="registration-search">Search registrations</label>
            <input
              type="text"
              className={inputCls}
              id="registration-search"
              name="Search"
              value={regFilters?.search}
              onChange={(e) => {
                setRegFilters({ ...regFilters, search: e.target.value, pageIndex: 0 });
              }}
              placeholder="Name, email, company, phone, or reference"
            />
          </div>
        </div>

        </div><div className="flex flex-wrap items-end gap-4">
        <div className="w-44">
          <div className={formGroupCls}>
            <label className={labelCls} htmlFor="StatusSearch">Status</label>
            <select id="StatusSearch"
              className={inputCls}
              name="StatusSearch"
              value={regFilters?.status}
              onChange={(e) => {
                setRegFilters({ ...regFilters, status: e.target.value, pageIndex: 0 });
              }}
            >
              <option value=""></option>
              <option value="Pending">Pending</option>
              <option value="Registered">All Registered</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Not Confirmed">Not Confirmed</option>
              <option value="No Show-Cancelled">No Show-Cancelled</option>

              <option value="Checked-In">Checked-In</option>
              <option value="Completed">Completed</option>
              <option value="Waitlist">Waitlist</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Self-Cancelled">Self-Cancelled</option>
            </select>
          </div>
        </div>
        {commItem &&
        <div className="w-44">
            <div className={formGroupCls}>
              <label className={labelCls} htmlFor="PaymentStatusSearch">Payment Status</label>
              <select id="PaymentStatusSearch"
                className={inputCls}
                name="PaymentStatusSearch"
                value={regFilters?.paymentStatus}
                onChange={(e) => {
                  setRegFilters({ ...regFilters, paymentStatus: e.target.value, pageIndex: 0 });
                }}
              >
                <option value=""></option>
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Waived">Waived</option>
              </select>
            </div>
          </div>
        }
        <div className="w-44">
          <div className={formGroupCls}>
            <label className={labelCls} htmlFor="WaiverSearch">Waiver</label>
            <select id="WaiverSearch"
              className={inputCls}
              name="WaiverSearch"
              value={regFilters?.waiver}
              onChange={(e) => {
                setRegFilters({ ...regFilters, waiver: e.target.value, pageIndex: 0 });
              }}
            >
              <option value=""></option>
              <option value="Yes">Complete</option>
              <option value="No">Incomplete</option>
            </select>
          </div>
        </div>
        {eventData?.allowGuestWaivers &&
        <div className="w-44">
            <div className={formGroupCls}>
              <label className={labelCls} htmlFor="GuestWaiverSearch">Guest Waiver</label>
              <select id="GuestWaiverSearch"
                className={inputCls}
                name="GuestWaiverSearch"
                value={regFilters?.guestWaiver}
                onChange={(e) => {
                  setRegFilters({ ...regFilters, guestWaiver: e.target.value, pageIndex: 0 });
                }}
              >
                <option value=""></option>
                <option value="Yes">Complete</option>
                <option value="No">Incomplete</option>
              </select>
            </div>
          </div>
        }
        {eventData?.allowCapacity && eventData?.capacityType === "session" && eventData?.sessionItems.length > 1 &&
        <div className="w-44">
            <div className={formGroupCls}>
              <label className={labelCls} htmlFor="ItemSearch">Item</label>
              <select id="ItemSearch"
                className={inputCls}
                name="ItemSearch"
                value={regFilters?.item}
                onChange={(e) => {
                  setRegFilters({ ...regFilters, item: e.target.value, pageIndex: 0 });
                }}
              >
                <option value=""></option>
                {eventData?.sessionItems.map((item, i) => (
                  <option value={i}>{item.name}</option>
                ))}
              </select>
            </div>
          </div>
        }
        {eventData?.allowCapacity && eventData?.capacityType === "session" &&
        <div className="w-44">
            <div className={formGroupCls}>
              <label className={labelCls} htmlFor="DaySearch">Day</label>
              <select id="DaySearch"
                className={inputCls}
                name="DaySearch"
                value={regFilters?.day}
                onChange={(e) => {
                  setRegFilters({ ...regFilters, day: e.target.value, pageIndex: 0 });
                }}
              >
                <option value=""></option>
                {dayList.map((item, i) => (
                  <option key={i} value={item}>{item}</option>
                ))}
              </select>
            </div>
          </div>
        }
        {eventData?.allowCapacity && eventData?.capacityType === "session" &&
        <div className="w-44">
            <div className={formGroupCls}>
              <label className={labelCls} htmlFor="SessionSearch">Session</label>
              <select id="SessionSearch"
                className={inputCls}
                name="SessionSearch"
                value={regFilters?.session}
                onChange={(e) => {
                  setRegFilters({ ...regFilters, session: e.target.value, pageIndex: 0 });
                }}
              >
                <option value=""></option>
                {sessionList.map((item, i) => (
                  <option value={item}>{item}</option>
                ))}
              </select>
            </div>
          </div>
        }

                {eventData?.allowPasscodes && eventData?.allowMultiUsePasscodes &&
          <div className="w-1/6">
            <div className={formGroupCls}>
              <label className={labelCls} htmlFor="PasscodeSearch">Passcode</label>
              <select id="PasscodeSearch"
                className={inputCls}
                name="PasscodeSearch"
                value={regFilters?.passcode}
                onChange={(e) => {
                  setRegFilters({ ...regFilters, passcode: e.target.value, pageIndex: 0 });
                }}
              >
                <option value=""></option>
                {eventData?.multiUsePasscodes.map((item, i) => {
                  // console.log('test');
                  if (item && (!item.usersAllowed || item.usersAllowed.length === 0 || item.usersAllowed?.find(o => o.name === user.email)))
                    return (
                      <option value={item.passcode}>{item.passcode}</option>
                    )
                  return null;
                })}
              </select>
            </div>
          </div>
        }
                {eventData?.allowPushToCap &&
          <div className="w-1/6">
            <div className={formGroupCls}>
              <label className={labelCls} htmlFor="LeadSentSearch">Sent To C@P</label>
              <select id="LeadSentSearch"
                className={inputCls}
                name="LeadSentSearch"
                value={regFilters?.leadSent}
                onChange={(e) => {
                  setRegFilters({ ...regFilters, leadSent: e.target.value, pageIndex: 0 });
                }}
              >
                <option value=""></option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>

              </select>
            </div>
          </div>
        }



      </div>
          <div className="flex flex-wrap items-end gap-4">
                     <div className={formGroupCls} style={{ maxWidth: "200px" }}>
            <label className={labelCls} htmlFor="SortOrder">Sort Order</label>
            <select id="SortOrder"
              className={inputCls}

              name="SortOrder"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                }}
              >
                <option value="createdAt|desc">Created Last</option>
                <option value="firstName|asc">First Name A-Z</option>
                <option value="firstName|desc">First Name Z-A</option>
                <option value="lastName|asc">Last Name A-Z</option>
                <option value="lastName|desc">Last Name Z-A</option>
                <option value="email|asc">Email A-Z</option>
                <option value="email|desc">Email Z-A</option>
                <option value="sessionDate|sessionTime|asc">Session &darr;</option>
                <option value="sessionDate|sessionTime|desc">Session &uarr;</option>
              </select>
              </div>
            <div className="flex-1"></div>



                       <div className="flex-none">
          <button
            className={secondaryBtnCls}

            onClick={() => {
              setRegFilters(DEFAULT_REG_FILTERS);
            }}
          >
            Clear filters
          </button>
          </div>
            <div ref={fieldPicker} className="field-container flex-none">
              <button type="button" className={secondaryBtnCls} aria-expanded={showFieldList} aria-controls="registration-columns"
                onClick={() => setShowFieldList((value) => !value)}>Columns</button>
            <div id="registration-columns" className={showFieldList ? "field-list field-list-show" : "field-list "}>
              <p className="mb-3 text-sm font-semibold">Visible columns</p>
              <p className="mb-3 text-xs">Saved for this event on this device.</p>
              {Object.entries(fieldList).map((key, i) => (
                // {key[0] ===}
                <div key={key[0]} className={availableFieldList.indexOf(key[0]) > -1 ? "mb-2" : "hidden"}>
                  <label className="checkbox-container">
                    {fieldLabel(key[0], eventData?.additionalFields)}
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        setFieldList({
                          ...fieldList,
                          [key[0]]: e.target.checked,
                        });
                        setStorageUnavailable(!writeRegistrationsTableSetting(eventData?.id || id, "fieldList", {
                          ...fieldList,
                          [key[0]]: e.target.checked,
                        }));
                      }}
                      id={key[0] + "_cb"}
                      name={key[0] + "_cb"}
                      checked={fieldList[key[0]] || false}
                      value={fieldList[key[0]] || false}
                    />
                    <span className="checkmark"></span>
                  </label>
                </div>
              ))}
            </div>
          </div>
            </div>
            <div className="py-2"></div>
            {storageUnavailable && <p role="status" className="mb-3 text-sm text-amber-800 dark:text-amber-300">Browser storage is unavailable. Your view works for this visit, but preferences cannot be saved on this device.</p>}
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm">
              <span role="status" className="tabular-nums">{data.filtered?.length ? `${indexOfFirstReg + 1}–${Math.min(indexOfLastReg, data.filtered.length)} of ${data.filtered.length} matching registrations` : '0 matching registrations'}</span>
              <div className="flex flex-wrap items-center gap-2"><label htmlFor="registration-page-size">Rows per page</label><select id="registration-page-size" className={inputCls} style={{ width: '6rem', marginTop: 0 }} value={regsPerPage} onChange={(event) => {
                const value = Number(event.target.value); setRegsPerPage(value); setRegFilters((filters) => ({ ...filters, pageIndex: 0 }));
                setStorageUnavailable(!writeRegistrationsTableSetting(id, 'pageSize', value));
              }}>{[50, 100, 250, 500].map((value) => <option key={value} value={value}>{value}</option>)}</select>
              <button type="button" className={secondaryBtnCls} disabled={data.isFetching} onClick={() => fetch()}>Refresh</button></div>
            </div>
            <Fetching />
            <Message />
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200 text-sm dark:divide-zinc-700" >
              <thead className="bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              <tr>
                {(createReminder || createReminderSMS) &&
                <th className="table-cell px-3 py-2">
                  <label className="checkbox-container">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                        // alert(e.target.value);
                        if (!allSelected) {
                          setAllSelected(true);
                          let tmpList = [];
                          for (let i = 0; i < data?.filtered?.length; i++) {
                            tmpList.push(i);
                          }
                          setReminderEmailList(tmpList);
                          var ele = document.getElementsByName('reminderCheck');
                          for (var i = 0; i < ele.length; i++) {
                            if (ele[i].type === 'checkbox')
                              ele[i].checked = true;
                          }
                        } else {
                          setAllSelected(false);
                          setReminderEmailList([]);
                          let ele = document.getElementsByName('reminderCheck');
                          for (let i = 0; i < ele.length; i++) {
                            if (ele[i].type === 'checkbox')
                              ele[i].checked = false;
                          }
                        }
                      }}
                      aria-label={`Select all ${data.filtered?.length || 0} matching registrations`}
                      id="reminderCheckAll"
                      name="reminderCheckAll"
                      checked={allSelected}
                    />
                    <span className="checkmark"></span>
                  </label>
                </th>
              }
              {fieldList['company'] &&
                <th className="table-cell px-3 py-2">Company</th>
              }
              {fieldList['title'] &&
                <th className="table-cell px-3 py-2">Title</th>
              }
              {(fieldList['firstName'] || fieldList['lastName']) &&
                <th className="table-cell px-3 py-2">Name</th>
              }
     
              {fieldList['email'] &&
                <th className="table-cell px-3 py-2">Email</th>
              }
              {fieldList['phone'] &&
                <th className="table-cell px-3 py-2">Phone</th>
              }
              {fieldList['address1'] &&
                <th className="table-cell px-3 py-2">Address 1</th>
              }
              {fieldList['address2'] &&
                <th className="table-cell px-3 py-2">Address 2</th>
              }
              {fieldList['city'] &&
                <th className="table-cell px-3 py-2">City</th>
              }
              {fieldList['state'] &&
                <th className="table-cell px-3 py-2">State</th>
              }
              {fieldList['postal'] &&
                <th className="table-cell px-3 py-2">Postal</th>
              }
              {fieldList['createdDate'] &&
                <th className="table-cell px-3 py-2">Created</th>
              }
              {fieldList['updatedDate'] &&
                <th className="table-cell px-3 py-2">Updated</th>
              }
              {fieldList['status'] &&
                <th className="table-cell px-3 py-2">Status</th>
              }
              {commItem && fieldList['paymentStatus'] ?
                <th className="table-cell px-3 py-2">Payment Status</th>
                : <></>}
              {eventData?.allowGuests && fieldList['guest'] &&
                <th className="table-cell px-3 py-2">Guest</th>
              }
              {eventData?.allowGuests && fieldList['guestEmail'] &&
                <th className="table-cell px-3 py-2">Guest Email</th>
              }
              {fieldList['itemInfo'] &&
                <th className="table-cell px-3 py-2">Item</th>
              }
              {fieldList['sessionInfo'] &&
                <th className="table-cell px-3 py-2">Session</th>
              }
              {eventData?.allowWaivers && fieldList['waiver'] &&
                <th className="table-cell px-3 py-2">Waiver</th>
              }
              {eventData?.allowGuestWaivers && fieldList['guestWaiver'] &&
                <th className="table-cell px-3 py-2">Guest Waiver</th>
              }
              {!eventData?.disableQuickCheckIn && 
              <th className="table-cell px-3 py-2">Check-In</th>
              }
              {fieldList['passcode'] &&
                <th className="table-cell px-3 py-2">Passcode</th>
              }
              {fieldList['dealer_optin'] &&
                <th className="table-cell px-3 py-2">Dealer OptIn</th>
              }
              {fieldList['dealer'] &&
                <th className="table-cell px-3 py-2">Pref Dealer</th>
              }
              {fieldList['experience'] &&
                <th className="table-cell px-3 py-2">Experience</th>
              }
              {fieldList['modelInterest'] &&
                <th className="table-cell px-3 py-2">Model Interest</th>
              }
              {fieldList['purchaseTimeFrame'] &&
                <th className="table-cell px-3 py-2">Purchase Timeframe</th>
              }
              {fieldList['disableLead'] &&
                <th className="table-cell px-3 py-2">Do Not Send C@P</th>
              }
              {fieldList['leadSent'] &&
                <th className="table-cell px-3 py-2">Sent to C@P</th>
              }
              {eventData?.additionalFields?.map((f, i) => (<>
                <th key={i} className={fieldList[f.fieldName] ? "table-cell px-3 py-2" : "hidden"}>{f.fieldLabel}</th>
                {f.fieldType === "CommerceItem" &&
                  <th key={100 + i} className={fieldList[f.fieldName + "Cost"] ? "table-cell px-3 py-2" : "hidden"}>{f.fieldLabel} Cost</th>
                }
              </>
              ))}
              {commItem && eventData?.paymentTax && fieldList['subCost'] ?
                <th className="table-cell px-3 py-2">Subtotal</th>
                : <></>}
              {commItem && eventData?.paymentTax && fieldList['taxCost'] ?
                <th className="table-cell px-3 py-2">Tax</th>
                : <></>}
              {commItem && fieldList['totalCost'] ?
                <th className="table-cell px-3 py-2">Total</th>
                : <></>}
              {commItem && fieldList['adyenRef'] ?
                <th className="table-cell px-3 py-2">Adyen Ref</th>
                : <></>}
              {commItem && fieldList['adyenLink'] ?
                <th className="table-cell px-3 py-2">Adyen Link</th>
                : <></>}
              {fieldList['notes'] &&
                <th className="table-cell px-3 py-2">Notes</th>
              }
            </tr>
          </thead>
          <tbody>
            {currentRegs?.map((r, pageOffset) => {
              const i = indexOfFirstReg + pageOffset;
              return (
              <tr
                key={i}
                className={alertRules(r) + "test my-2 pointer"}
              >
                {(createReminder || createReminderSMS) &&
                  <td className="table-cell px-3 py-2">                         <label className="checkbox-container">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        let tmpList = [...reminderEmailList];
                        if (reminderEmailList.indexOf(i) < 0) {
                          tmpList.push(i);
                        } else {
                          setAllSelected(false);
                          tmpList.splice(tmpList.indexOf(i), 1);
                        }
                        setReminderEmailList(tmpList);
                      }}
                      aria-label={`Select ${r.firstName || ""} ${r.lastName || ""} registration`}
                      id={"reminderCheck" + i}
                      name="reminderCheck"
                      checked={reminderEmailList.includes(i)}
                    />
                    <span className="checkmark"></span>
                  </label>
                  </td>
                }
                {fieldList['company'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.company}
                  </td>
                }
                {fieldList['title'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.title}
                  </td>
                }
                {(fieldList['firstName'] || fieldList['lastName']) &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    <Link to={"/manage/registration/" + r.id} onClick={(event) => event.stopPropagation()}
                      className="underline underline-offset-4" aria-label={`Open registration for ${r.firstName || ''} ${r.lastName || ''}`}>
                      {[fieldList.firstName && r.firstName, fieldList.lastName && r.lastName].filter(Boolean).join(' ') || r.id}
                    </Link>
                  </td>
                }
                
                {fieldList['email'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    <Link to={"/manage/registration/" + r.id} onClick={(event) => event.stopPropagation()} aria-label={`Open registration for ${r.email}`}>{r.email}</Link>
                  </td>
                }
                {fieldList['phone'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.phone}
                  </td>
                }
                {fieldList['address1'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.address1}
                  </td>
                }
                {fieldList['address2'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.address2}
                  </td>
                }
                {fieldList['city'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.city}
                  </td>
                }
                {fieldList['state'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.state}
                  </td>
                }
                {fieldList['postal'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.postal}
                  </td>
                }
                {fieldList['createdDate'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.createdDate}
                  </td>
                }
                {fieldList['updatedDate'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.updatedDate}
                  </td>
                }
                {fieldList['status'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {getRegistrationStatusLabel(r)}
                  </td>
                }
                {commItem && fieldList['paymentStatus'] ?
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.adyenPaymentStatus}
                  </td>
                  : <></>}
                {r.guestId && eventData?.allowGuests && fieldList['guest'] && (
                  <td
                    className="table-cell px-3 py-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      history("/manage/registration/" + r.guestId);
                    }}
                  >
                    View Guest
                  </td>
                )}
                {r.parentId && eventData?.allowGuests && fieldList['guest'] && (
                  <td
                    className="table-cell px-3 py-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      history("/manage/registration/" + r.parentId);
                    }}
                  >
                    View Primary
                  </td>
                )}
                {!r.parentId && !r.guestId && eventData?.allowGuests && fieldList['guest'] && (
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                  >
                    {r.guestFirstName ? <div>{r.guestFirstName}</div> : (!r.guestLastName ? "--" : "")}
                    {r.guestLastName ? <div>{r.guestLastName}</div> : (r.guestLastName ? "--" : "")}
                  </td>
                )}
                                {!r.parentId && !r.guestId && eventData?.allowGuests && fieldList['guestEmail'] && (
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                  >
                    {r.guestEmail ? r.guestEmail : "--"}
                  </td>
                )}
                {fieldList['itemInfo'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.sessionItemId !== null && r.sessionItemId !== undefined && +r.sessionItemId >= 0 ? eventData?.sessionItems[+r.sessionItemId].name : ""}
                  </td>
                }
                {fieldList['sessionInfo'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                    >
                    <div>{r.sessionDate  && r.sessionTime ? (() => {
                      // Combine sessionDate and sessionTime, parse as date, format as "ddd MMM D YYYY h:mm A"
                      const dateStr = `${r.sessionDate} ${r.sessionTime}`;
                      const m = moment(dateStr, "dddd MMMM Do YYYY");
                      return m.isValid() ? m.format("ddd MMM D YYYY") : dateStr;
                    })() : ""}
                    </div>
                    <div>
                    {r.sessionLabel || r.sessionTime || "" }
                    </div>
                    </td>
                  }
                  {eventData?.allowWaivers && fieldList['waiver'] &&
                    <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.hasWaiver
                      ? "Complete"
                      : eventData?.allowWaivers
                        ? "Incomplete"
                        : "N/A"}
                  </td>
                }
                {eventData?.allowGuestWaivers && fieldList['guestWaiver'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.hasGuestWaiver
                      ? "Complete"
                      : eventData?.allowGuestWaivers && (r.guestFirstName || r.guestLastName)
                        ? "Incomplete"
                        : "N/A"}
                  </td>
                }
                              {!eventData?.disableQuickCheckIn && 

                <td className="table-cell px-3 py-2">
                  {(r.status === "Registered" || r.status === "Checked-In") &&
                    (r.hasWaiver || !eventData?.allowWaivers) && (r.hasGuestWaiver || !eventData?.allowGuestWaivers || (!r.guestFirstName && !r.guestLastName)) && (
                      <button type="button"
                        className="inline-flex min-h-10 min-w-10 items-center justify-center"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          history(`/manage/registration/${r.id}#registration-check-in`);
                        }}
                        aria-label={`Open check-in for ${r.firstName || ""} ${r.lastName || ""}`}
                        title="Open attendee, guest, and item check-in"
                      >
                        <span className="block size-5"><CheckIcon /></span>
                      </button>
                    )}
                </td>
            }
                {fieldList['passcode'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.passcode} {getPasscodeDescription(r.passcode)}
                  </td>
                }
                {fieldList['dealer_optin'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.dealer_optin ? "Yes" : "No"}
                  </td>
                }
                {fieldList['dealer'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {dealers.find((d) => d.code === r.dealer)?.name} {r.dealer && r.dealer !== "--" ? "(" + r.dealer + ")" : ""}
                  </td>
                }
                {fieldList['experience'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {getExperience(r.experience)}
                  </td>
                }
                {fieldList['modelInterest'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {getModelInterest(r.modelInterest)}
                  </td>
                }
                {fieldList['purchaseTimeFrame'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {getPurchaseTimeFrame(r.purchaseTimeFrame)}
                  </td>
                }
                {fieldList['disableLead'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.disableLead ? "Yes" : "No"}
                  </td>
                }
                {fieldList['leadSent'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.leadSent ? "Yes" : "No"}
                  </td>
                }
                {eventData?.additionalFields?.map((f, i) => (
                  <>
                    <td key={i}
                      onClick={() => {
                        history("/manage/registration/" + r.id);
                      }}
                      className={fieldList[f.fieldName] ? "table-cell px-3 py-2" : "hidden"}
                    >
                      {f.fieldType === "Checkbox" ? r[f.fieldName] ? "Yes" : "No" : r[f.fieldName]}
                    </td>
                    {f.fieldType === "CommerceItem" &&
                      <td key={i + 100}
                        onClick={() => {
                          history("/manage/registration/" + r.id);
                        }}
                        className={fieldList[f.fieldName + "Cost"] ? "table-cell px-3 py-2" : "hidden"}
                      >
                        {r[f.fieldName + "Cost"]}
                      </td>
                    }
                  </>
                ))}
                {commItem && eventData?.paymentTax && fieldList['subCost'] ?
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.subCost}
                  </td>
                  : <></>}
                {commItem && eventData?.paymentTax && fieldList['taxCost'] ?
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.taxCost}
                  </td>
                  : <></>}
                {commItem && fieldList['totalCost'] ?
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.totalCost}
                  </td>
                  : <></>}
                {commItem && fieldList['adyenRef'] ?
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.adyenRef}
                  </td>
                  : <></>}
                {commItem && fieldList['adyenLink'] ?
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.adyenLink}
                  </td>
                  : <></>}
                {fieldList['notes'] &&
                  <td
                    onClick={() => {
                      history("/manage/registration/" + r.id);
                    }}
                    className="table-cell px-3 py-2"
                  >
                    {r.notes}
                  </td>
                }
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {!data.isFetching && !data.message && !currentRegs?.length && <p className="my-6 text-sm text-zinc-600 dark:text-zinc-400">No registrations match your filters. Clear the filters to view all registrations for this event.</p>}
        <Pagination
        regsPerPage={regsPerPage}
        totalRegs={data.filtered?.length || 0}
        paginate={paginate}
        currentPage={currentPageIndex + 1}
      />
      <RegistrationActions
        role={user.role}
        eventId={eventData?.id || id}
        allowWaivers={eventData?.allowWaivers}
        allowGuestWaivers={eventData?.allowGuestWaivers}
        allSubmissions={allSubmissions}
        downloadAllWaivers={downloadAllWaivers}
        downloadAllGuestWaivers={downloadAllGuestWaivers}
        downloadSubmissions={downloadSubmissions}
        clearLeadData={clearLeadData}
        deleteAllRegistrations={deleteAllRegistrations}
      />
    </div>
  );
};
const RegistrationsRoute = () => {
  const { id: routeId } = useParams();
  const { currentEventData } = useContext(CurrentEventDataContext);
  const id = routeId || currentEventData?.id;
  return <Registrations key={id || 'unselected'} id={id} />;
};
export default RegistrationsRoute;
